import { Button } from '@assembly-js/design-system';
import React from 'react';

import { DANGER_BUTTON_CLASS } from 'src/components/DangerActionCard';
import { useAppDispatch } from 'src/hooks/useStore';
import { ActionButtonModal, ModalWrapper } from 'src/legacy/components/Modals';
import {
  useDeleteAppBuilderMutation,
  useGetAppBuilderDependentsQuery,
} from 'src/services/api/appBuilderApi';
import { alertSnackbar } from 'src/store/ui/actions';

import { DependentsWarning } from './DependentsWarning';

interface DeleteAppBuilderModalProps {
  appId: string;
  appName: string;
  onClose: () => void;
  onDeleted?: () => Promise<void> | void;
  /**
   * `danger` is app settings' compact confirm: who loses access as the
   * description and a red "Delete app" button, like deleting a workspace.
   */
  appearance?: 'default' | 'danger';
  /** The danger confirm's description. */
  description?: string;
  /** See `deleteAppBuilder`'s `fromBuilderPage`. */
  fromBuilderPage?: boolean;
}

export const DeleteAppBuilderModal: React.FC<DeleteAppBuilderModalProps> = ({
  appId,
  appName,
  onClose,
  onDeleted,
  appearance = 'default',
  description,
  fromBuilderPage = false,
}) => {
  const dispatch = useAppDispatch();
  const [deleteBuilder, { isLoading }] = useDeleteAppBuilderMutation();
  const {
    data: dependents,
    isLoading: isDependentsLoading,
    error: dependentsError,
  } = useGetAppBuilderDependentsQuery(
    { id: appId },
    { refetchOnMountOrArgChange: true },
  );
  // Fail-soft: the dialog confirms without the warning rather than blocking the delete.
  React.useEffect(() => {
    if (dependentsError) console.error(dependentsError);
  }, [dependentsError]);

  const handleConfirm = async () => {
    try {
      await deleteBuilder({ id: appId, fromBuilderPage }).unwrap();
    } catch (error) {
      console.error(error);
      void dispatch(alertSnackbar({ errorMessage: 'Unable to delete app' }));
      return;
    }
    // The delete succeeded — any failure past this point (e.g. navigation
    // or cache cleanup) must not surface as "Unable to delete app".
    try {
      await onDeleted?.();
    } catch (error) {
      console.error(error);
    }
    onClose();
  };

  if (appearance === 'danger') {
    return (
      <ModalWrapper
        open
        onClose={onClose}
        // Can't be dismissed mid-delete, like the default confirm.
        isLoading={isLoading}
        width={440}
        hideDividers
        hideActions
        denseContent
        title={`Delete ${appName}`}
        description={description}
      >
        <div className="px-4 sm:px-5">
          <DependentsWarning appName={appName} dependents={dependents} />
        </div>
        <div className="flex justify-end gap-2 px-4 pb-4 sm:px-5">
          <Button
            type="button"
            variant="secondary"
            label="Cancel"
            disabled={isLoading}
            onClick={onClose}
          />
          <Button
            type="button"
            label="Delete app"
            className={DANGER_BUTTON_CLASS}
            loading={isLoading}
            disabled={isLoading || isDependentsLoading}
            onClick={() => void handleConfirm()}
          />
        </div>
      </ModalWrapper>
    );
  }

  return (
    <ActionButtonModal
      open
      title={`Delete app "${appName}"?`}
      mainComponent={
        <>
          <DependentsWarning appName={appName} dependents={dependents} />
          {`This will permanently delete ${appName}. This action cannot be undone. You will not be able to recover your app once it has been deleted.`}
        </>
      }
      positiveAction
      positiveActionText="Delete"
      cancelActionText="Cancel"
      onPositiveActionPerformed={() => void handleConfirm()}
      onClose={onClose}
      isLoading={isLoading}
      disableSuccessButton={isDependentsLoading}
    />
  );
};
